import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import Layout from '../../components/Layout';
import { getBank, listGroups, updateBank } from '../../api/banks';
import './BanqueDetailPage.css';

const BanqueDetailPage = () => {
  const { id } = useParams();

  const [bank, setBank] = useState(null);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError('');
    Promise.all([getBank(id), listGroups()])
      .then(([bankData, groupsData]) => {
        if (cancelled) return;
        setBank(bankData);
        setGroups(groupsData);
      })
      .catch(() => { if (!cancelled) setError('Impossible de charger ce bank.'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [id]);

  const updateField = (field, value) => {
    setBank((prev) => ({ ...prev, [field]: value }));
    setSaved(false);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    setSaved(false);
    try {
      const updated = await updateBank(id, {
        name: bank.name,
        address: bank.address,
        phone: bank.phone,
        comment: bank.comment,
        group: bank.group || null,
        sale_enabled: bank.sale_enabled,
        can_nullify: bank.can_nullify,
      });
      setBank(updated);
      setSaved(true);
    } catch (err) {
      setError('Sauvegarde impossible — verifie les champs et le serveur backend.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <p className="bqd-status">Chargement...</p>
      </Layout>
    );
  }

  if (!bank) {
    return (
      <Layout>
        <p className="bqd-status bqd-error">{error || 'Bank introuvable.'}</p>
        <Link to="/parametres/banque" className="bqd-back">◀ Retour a la recherche</Link>
      </Layout>
    );
  }

  return (
    <Layout>
      <Link to="/parametres/banque" className="bqd-back">◀ Retour a la recherche</Link>

      <div className="bqd-card">
        <div className="bqd-card-header">
          <span className="bqd-card-title">Bank Info</span>
        </div>
        <form className="bqd-card-body" onSubmit={handleSave}>
          <div className="bqd-row">
            <label className="bqd-label">ID</label>
            <span className="bqd-value">
              {bank.seq}
              {/* Meme valeur que "REF:" imprime sur les fiches du POS (ex. 4 -> 04-04). */}
              {bank.seq != null && (
                <> &nbsp;—&nbsp; Ref fiche : <strong>{String(bank.seq).padStart(2, '0')}-{String(bank.seq).padStart(2, '0')}</strong></>
              )}
            </span>
          </div>

          <div className="bqd-row">
            <label className="bqd-label">Username</label>
            <span className="bqd-value">{bank.username || '—'}</span>
          </div>

          <div className="bqd-row">
            <label className="bqd-label">Group</label>
            <select
              className="bqd-input"
              value={bank.group ?? ''}
              onChange={(e) => updateField('group', e.target.value ? Number(e.target.value) : null)}
            >
              <option value="">—</option>
              {groups.map((g) => (
                <option key={g.id} value={g.id}>{g.display_name || g.name}</option>
              ))}
            </select>
          </div>

          <div className="bqd-row">
            <label className="bqd-label">Superviseur(s)</label>
            <span className="bqd-value">
              {(bank.supervisor_names || []).join(', ') || '—'}
              {' '}
              <Link to="/parametres/superviseurs" className="bqd-link">(gérer)</Link>
            </span>
          </div>

          <div className="bqd-row">
            <label className="bqd-label">Name</label>
            <input
              className="bqd-input"
              type="text"
              value={bank.name}
              onChange={(e) => updateField('name', e.target.value)}
            />
          </div>

          <div className="bqd-row">
            <label className="bqd-label">Address</label>
            <input
              className="bqd-input"
              type="text"
              value={bank.address}
              onChange={(e) => updateField('address', e.target.value)}
            />
          </div>

          <div className="bqd-row">
            <label className="bqd-label">Phone</label>
            <input
              className="bqd-input"
              type="text"
              value={bank.phone}
              onChange={(e) => updateField('phone', e.target.value)}
            />
          </div>

          <div className="bqd-row bqd-row-top">
            <label className="bqd-label">Comments</label>
            <textarea
              className="bqd-textarea"
              value={bank.comment}
              onChange={(e) => updateField('comment', e.target.value)}
            />
          </div>

          <label className="bqd-checkbox-label">
            <input
              type="checkbox"
              checked={bank.sale_enabled}
              onChange={(e) => updateField('sale_enabled', e.target.checked)}
            />
            Enable Sale?
          </label>

          <label className="bqd-checkbox-label">
            <input
              type="checkbox"
              checked={!!bank.can_nullify}
              onChange={(e) => updateField('can_nullify', e.target.checked)}
            />
            Autoriser l&apos;annulation de fiches (POS)
          </label>

          {error && <p className="bqd-error">{error}</p>}
          {saved && <p className="bqd-saved">Sauvegarde.</p>}

          <button type="submit" className="bqd-save-btn" disabled={saving}>
            {saving ? 'Sauvegarde...' : 'Sauver'}
          </button>
        </form>
      </div>
    </Layout>
  );
};

export default BanqueDetailPage;
